import type {
  GetCustomerPortalCreditPoolsResponse,
  GetCustomerPortalDailyUsageDataRequestPayload,
  GetCustomerPortalDailyUsageDataResponsePayload,
  GetCustomerPortalDataResponsePayload,
  GetCustomerPortalInvoicePdfResponsePayload,
  GetCustomerPortalMonthlyUsageDataRequestPayload,
  GetCustomerPortalMonthlyUsageDataResponsePayload,
} from '@vy/api-definition';
import {
  GetCustomerPortalCreditPoolsResponseSchema,
  GetCustomerPortalDailyUsageDataResponseSchema,
  GetCustomerPortalDataResponseSchema,
  GetCustomerPortalInvoicePdfResponseSchema,
  GetCustomerPortalMonthlyUsageDataResponseSchema,
} from '@vy/api-definition';
import { withDefault } from '@vy/utils';
import type { UseMutationOptions, UseQueryOptions } from 'react-query';
import { useMutation, useQuery } from 'react-query';
import type {
  GetCustomerPortalDataRequest,
  GetCustomerPortalInvoicePdfRequest,
} from '../../../../../../libs/client/src/generated/apis/DefaultApi';
import { usePageParam } from '../../../hooks/search-params/use-page-param';
import { usePageSizeParam } from '../../../hooks/search-params/use-page-size-param';
import { getPublicApi } from '../../../services/api';
import { queryKeys } from '../../../services/query-key/query-keys';
import { CUSTOMER_PORTAL_PAGE_SIZE } from './use-get-customer-portal-consts';
import { CustomUseQueryOptions } from '../requests-types/custom-use-query-options';

export function useGetCustomerPortalData(
  payload: GetCustomerPortalDataRequest,
  options?: CustomUseQueryOptions<GetCustomerPortalDataResponsePayload>,
) {
  return useQuery({
    queryKey: queryKeys.customerPortalData(...Object.values(payload)),
    queryFn: async () => {
      const publicApi = getPublicApi();
      const customerPortalData = await publicApi.getCustomerPortalData({
        ...payload,
        pageSize: withDefault(payload.pageSize, CUSTOMER_PORTAL_PAGE_SIZE),
        page: withDefault(payload.page, 0),
      });

      return GetCustomerPortalDataResponseSchema.parse(customerPortalData);
    },
    keepPreviousData: true,
    ...options,
  });
}

export function useGetCustomerPortalDataWithPageParams(
  token: string,
  options?: CustomUseQueryOptions<GetCustomerPortalDataResponsePayload>,
) {
  const [page] = usePageParam();
  const [pageSize] = usePageSizeParam();

  return useGetCustomerPortalData(
    {
      token,
      page,
      pageSize,
    },
    options,
  );
}

export function useGetCustomerPortalMonthlyUsageData(
  payload: GetCustomerPortalMonthlyUsageDataRequestPayload,
  options?: UseQueryOptions<GetCustomerPortalMonthlyUsageDataResponsePayload>,
) {
  return useQuery({
    queryKey: queryKeys.customerPortalMonthlyUsageData(payload),
    queryFn: async () => {
      const publicApi = getPublicApi();
      const monthlyUsageData = await publicApi.getCustomerPortalMonthlyUsageData(payload);

      return GetCustomerPortalMonthlyUsageDataResponseSchema.parse(monthlyUsageData);
    },
    suspense: true,
    ...options,
  });
}

export function useGetCustomerPortalDailyUsageData(
  payload: GetCustomerPortalDailyUsageDataRequestPayload,
  options?: UseQueryOptions<GetCustomerPortalDailyUsageDataResponsePayload>,
) {
  return useQuery({
    queryKey: queryKeys.customerPortalDailyUsageData(payload),
    queryFn: async () => {
      const publicApi = getPublicApi();
      const dailyUsageData = await publicApi.getCustomerPortalDailyUsageData(payload);

      return GetCustomerPortalDailyUsageDataResponseSchema.parse(dailyUsageData);
    },
    ...options,
  });
}

export function useGetCustomerPortalInvoicePdf(token: string, invoiceId: string) {
  return useQuery({
    queryKey: queryKeys.customerPortalInvoicePdf(token, invoiceId),
    queryFn: async () => {
      const publicApi = getPublicApi();
      const customerPortalInvoicePdf = await publicApi.getCustomerPortalInvoicePdf({
        token,
        invoiceId,
      });

      return GetCustomerPortalInvoicePdfResponseSchema.parse(customerPortalInvoicePdf);
    },
  });
}

export function useGetCustomerPortalInvoicePdfMutation(
  options?: UseMutationOptions<
    GetCustomerPortalInvoicePdfResponsePayload,
    unknown,
    GetCustomerPortalInvoicePdfRequest
  >,
) {
  return useMutation({
    mutationFn: async (payload) => {
      const publicApi = getPublicApi();
      const customerPortalInvoicePdf = await publicApi.getCustomerPortalInvoicePdf(payload);

      return GetCustomerPortalInvoicePdfResponseSchema.parse(customerPortalInvoicePdf);
    },
    ...options,
  });
}

export function useGetCustomerPortalCreditPools(
  token: string,
  options?: CustomUseQueryOptions<GetCustomerPortalCreditPoolsResponse>,
) {
  return useQuery({
    queryKey: queryKeys.customerPortalCreditPools(token),
    queryFn: async () => {
      const publicApi = getPublicApi();
      const creditPools = await publicApi.getCustomerPortalCreditPools({ token });

      return GetCustomerPortalCreditPoolsResponseSchema.parse(creditPools);
    },
    suspense: false,
    ...options,
  });
}
